import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Switch,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import { toast } from "react-toastify";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import ColorBoxModal from "../ColorBoxModal/index.js";
import useAerosSettingsStyles from "./aerosSettingsStyles";

// Traduz o código de erro da API para uma mensagem legível: os toasts
// exibiam o código cru (ex.: "ERR_INVALID_REASON_COLOR") para o usuário.
const API_ERRORS = {
  ERR_INVALID_REASON_ORDER: "Não foi possível reordenar os motivos.",
  ERR_INVALID_REASON_COLOR: "Cor inválida. Use um hexadecimal como #A044FF.",
  ERR_DUPLICATED_FINALIZATION_REASON: "Já existe um motivo com esse nome.",
};

const apiErrorMessage = (err, fallback) => {
  const code = err?.response?.data?.error;
  return API_ERRORS[code] || code || fallback;
};

// Aba "Finalização do Atendimento" de /settings, fiel ao mockup Aeros Dark
// ("Configurações · Finalização"): cabeçalho com o toggle "Informar valor da
// venda", linha de adição inline e lista de motivos em card. Endpoints e
// handlers preservados (/ticketFinalizationReasons e informarValorVenda via
// /companySettings).
const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
    toggleLabel: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
    },
    addRow: {
      display: "flex",
      gap: 10,
      marginBottom: 12,
      alignItems: "flex-start",
    },
    addInput: {
      flex: 1,
      minWidth: 0,
      "& input::placeholder": {
        color: theme.palette.aeros.textTertiary,
        opacity: 1,
      },
    },
    addButton: {
      padding: "9px 14px",
      flexShrink: 0,
    },
    reasonRow: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "11px 14px",
      borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.05)"}`,
      "&:last-child": { borderBottom: "none" },
      "&:hover": { backgroundColor: "rgba(160,68,255,0.04)" },
    },
    reasonBody: {
      flex: 1,
      minWidth: 0,
    },
    reasonName: {
      fontSize: 13.5,
      fontWeight: 500,
      color: theme.palette.text.primary,
      lineHeight: 1.3,
      overflowWrap: "anywhere",
    },
    reasonDesc: {
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      lineHeight: 1.4,
      marginTop: 2,
      overflowWrap: "anywhere",
    },
    reasonActions: {
      display: "flex",
      gap: 5,
      flexShrink: 0,
    },
    dialogField: {
      marginBottom: 12,
      "&:last-child": { marginBottom: 0 },
    },
    dragHandle: {
      display: "flex",
      color: theme.palette.aeros.textTertiary,
      cursor: "grab",
      "& .MuiSvgIcon-root": { fontSize: 16 },
    },
    colorDot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      backgroundColor: theme.palette.aeros.textTertiary,
      flexShrink: 0,
    },
    usageText: {
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
    },
    usageTrack: {
      width: 90,
      height: 5,
      borderRadius: 3,
      backgroundColor: isLight ? "rgba(15,20,40,0.08)" : "rgba(255,255,255,0.06)",
      overflow: "hidden",
      flexShrink: 0,
    },
    usageFill: {
      display: "block",
      height: "100%",
      backgroundColor: theme.palette.aeros.textTertiary,
    },
    swatchRow: {
      display: "flex",
      gap: 6,
      alignItems: "center",
      marginTop: 4,
    },
    swatch: {
      width: 28,
      height: 28,
      borderRadius: 8,
      border: "2px solid transparent",
      cursor: "pointer",
      padding: 0,
    },
    swatchActive: {
      borderColor: theme.palette.secondary.main,
    },
  };
});

const FinalizacaoAtendimento = ({ settings, onSettingsChange }) => {
  const aeros = useAerosSettingsStyles();
  const classes = useStyles();
  const [motivos, setMotivos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editingMotivo, setEditingMotivo] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    color: "",
  });
  const [informarValorVenda, setInformarValorVenda] = useState(false);
  const [requireReason, setRequireReason] = useState(false);
  const [usage, setUsage] = useState({});
  const [colorModalOpen, setColorModalOpen] = useState(false);
  const [newReasonName, setNewReasonName] = useState("");
  const [adding, setAdding] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    if (settings) {
      setInformarValorVenda(settings.informarValorVenda || false);
      setRequireReason(!!settings?.requireFinalizationReason);
    }
    fetchMotivos();
  }, [settings]);

  const fetchMotivos = async () => {
    try {
      const [{ data }, usageRes] = await Promise.all([
        api.get("/ticketFinalizationReasons"),
        api
          .get("/ticketFinalizationReasons/usage", { params: { days: 30 } })
          .catch(() => ({ data: [] })),
      ]);
      setMotivos(data);
      setUsage(
        Object.fromEntries((usageRes.data || []).map((u) => [u.name, u.count]))
      );
    } catch (err) {
      console.error("Erro ao buscar motivos:", err);
    }
  };

  const handleOpenModal = (motivo = null) => {
    if (motivo) {
      setEditingMotivo(motivo);
      setFormData({
        name: motivo.name,
        description: motivo.description || "",
        color: motivo.color || "",
      });
    } else {
      setEditingMotivo(null);
      setFormData({
        name: "",
        description: "",
        color: "",
      });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingMotivo(null);
    setFormData({
      name: "",
      description: "",
      color: "",
    });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Nome do motivo é obrigatório");
      return;
    }

    setLoading(true);
    try {
      const payload = { ...formData, color: formData.color || null };
      if (editingMotivo) {
        await api.put(
          `/ticketFinalizationReasons/${editingMotivo.id}`,
          payload
        );
        toast.success("Motivo atualizado com sucesso!");
      } else {
        await api.post("/ticketFinalizationReasons", payload);
        toast.success("Motivo criado com sucesso!");
      }
      handleCloseModal();
      fetchMotivos();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Erro ao salvar motivo"));
    }
    setLoading(false);
  };

  // Adição inline (mockup): cria o motivo só com o nome; a descrição fica
  // para o modal de edição.
  const handleQuickAdd = async () => {
    const name = newReasonName.trim();
    if (!name) {
      toast.error("Nome do motivo é obrigatório");
      return;
    }
    setAdding(true);
    try {
      await api.post("/ticketFinalizationReasons", { name, description: "" });
      toast.success("Motivo criado com sucesso!");
      setNewReasonName("");
      fetchMotivos();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Erro ao salvar motivo"));
    }
    setAdding(false);
  };

  const handleQuickAddKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!adding) handleQuickAdd();
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/ticketFinalizationReasons/${id}`);
      toast.success("Motivo excluído com sucesso!");
      fetchMotivos();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Erro ao excluir motivo"));
    }
  };

  const handleInformarValorVendaChange = async (event) => {
    const newValue = event.target.checked;
    setInformarValorVenda(newValue);

    try {
      await api.put("/companySettings", {
        column: "informarValorVenda",
        data: newValue,
      });

      if (onSettingsChange) {
        onSettingsChange({ ...settings, informarValorVenda: newValue });
      }

      toast.success("Configuração atualizada com sucesso!");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Erro ao atualizar configuração"));
      setInformarValorVenda(!newValue); // Revert on error
    }
  };

  const handleRequireReasonChange = async (event) => {
    const newValue = event.target.checked;
    setRequireReason(newValue);
    try {
      await api.put("/companySettings", {
        column: "requireFinalizationReason",
        data: newValue,
      });
      if (onSettingsChange) {
        onSettingsChange({ ...settings, requireFinalizationReason: newValue });
      }
      toast.success("Configuração atualizada com sucesso!");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Erro ao atualizar configuração"));
      setRequireReason(!newValue);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const reordered = Array.from(motivos);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setMotivos(reordered);
    try {
      await api.put("/ticketFinalizationReasons/reorder", {
        ids: reordered.map((m) => m.id),
      });
    } catch (err) {
      toast.error(apiErrorMessage(err, "Erro ao reordenar motivos"));
      fetchMotivos();
    }
  };

  const maxUsage = Math.max(0, ...Object.values(usage));

  return (
    <div>
      <div className={aeros.sectionHead}>
        <div>
          <div className={aeros.sectionTitle}>Motivos de finalização</div>
          <div className={aeros.sectionSubtitle}>
            O atendente escolhe um motivo ao resolver o ticket
          </div>
        </div>
        <div className={aeros.sectionActions}>
          <label className={aeros.toggleRow}>
            <span className={classes.toggleLabel}>Exigir motivo</span>
            <Switch
              className={aeros.toggleSwitch}
              checked={requireReason}
              onChange={handleRequireReasonChange}
              inputProps={{ "aria-label": "Exigir motivo" }}
            />
          </label>
          <label className={aeros.toggleRow}>
            <span className={classes.toggleLabel}>Informar valor da venda</span>
            <Switch
              className={aeros.toggleSwitch}
              checked={!!informarValorVenda}
              onChange={handleInformarValorVendaChange}
              inputProps={{ "aria-label": "Informar valor da venda" }}
            />
          </label>
        </div>
      </div>

      <div className={classes.addRow}>
        <TextField
          variant="outlined"
          placeholder='Novo motivo, ex: "Dúvida resolvida"'
          value={newReasonName}
          onChange={(e) => setNewReasonName(e.target.value)}
          onKeyDown={handleQuickAddKeyDown}
          disabled={adding}
          className={`${aeros.input} ${classes.addInput}`}
          inputProps={{ "aria-label": "Novo motivo" }}
        />
        <Button
          variant="contained"
          color="primary"
          className={`${aeros.gradientButton} ${classes.addButton}`}
          startIcon={<AddIcon />}
          onClick={handleQuickAdd}
          disabled={adding}
        >
          Adicionar
        </Button>
      </div>

      <div className={aeros.tableCard}>
        {motivos.length === 0 ? (
          <div className={aeros.emptyRow}>Nenhum motivo cadastrado</div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="finalization-reasons">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {motivos.map((motivo, index) => {
                    const count = usage[motivo.name] || 0;
                    const pct = maxUsage > 0 ? Math.round((count / maxUsage) * 100) : 0;
                    return (
                      <Draggable key={motivo.id} draggableId={String(motivo.id)} index={index}>
                        {(drag) => (
                          <div className={classes.reasonRow} ref={drag.innerRef} {...drag.draggableProps}>
                            <span
                              className={classes.dragHandle}
                              {...drag.dragHandleProps}
                              aria-label="Arrastar para reordenar"
                            >
                              <DragIndicatorIcon />
                            </span>
                            <span
                              className={classes.colorDot}
                              style={{ backgroundColor: motivo.color || undefined }}
                            />
                            <div className={classes.reasonBody}>
                              <div className={classes.reasonName}>{motivo.name}</div>
                              {motivo.description ? (
                                <div className={classes.reasonDesc}>{motivo.description}</div>
                              ) : null}
                            </div>
                            <span className={classes.usageText}>{count} usos · 30d</span>
                            <span className={classes.usageTrack}>
                              <span
                                className={classes.usageFill}
                                style={{ width: `${pct}%`, backgroundColor: motivo.color || undefined }}
                              />
                            </span>
                            <div className={classes.reasonActions}>
                              <IconButton
                                className={`${aeros.iconButton} ${aeros.iconButtonLg}`}
                                onClick={() => handleOpenModal(motivo)}
                                aria-label="Editar motivo"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                className={`${aeros.iconButton} ${aeros.iconButtonLg} ${aeros.iconButtonDanger}`}
                                onClick={() => setConfirmDelete(motivo)}
                                aria-label="Excluir motivo"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>

      <ConfirmationModal
        title="Excluir motivo"
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && handleDelete(confirmDelete.id)}
      >
        Tem certeza que deseja excluir este motivo?
      </ConfirmationModal>

      {/* Modal para Adicionar/Editar Motivo */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        classes={{ paper: aeros.dialogPaper }}
      >
        <DialogTitle disableTypography className={aeros.dialogTitle}>
          {editingMotivo ? "Editar motivo" : "Adicionar motivo"}
        </DialogTitle>
        <DialogContent className={aeros.dialogContent}>
          <div className={classes.dialogField}>
            <label className={aeros.fieldLabel} htmlFor="finalizacao-reason-name">
              Nome do motivo
            </label>
            <TextField
              id="finalizacao-reason-name"
              fullWidth
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              variant="outlined"
              className={aeros.input}
              required
              autoFocus
            />
          </div>
          <div className={classes.dialogField}>
            <label
              className={aeros.fieldLabel}
              htmlFor="finalizacao-reason-description"
            >
              Descrição (opcional)
            </label>
            <TextField
              id="finalizacao-reason-description"
              fullWidth
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              variant="outlined"
              className={aeros.input}
              multiline
              rows={3}
            />
          </div>
          <div className={classes.dialogField}>
            <span className={aeros.fieldLabel}>Cor</span>
            <div className={classes.swatchRow}>
              {["#A044FF", "#22D3EE", "#34D399", "#FBBF24", "#F87171"].map((hex) => (
                <button
                  key={hex}
                  type="button"
                  className={`${classes.swatch} ${formData.color === hex ? classes.swatchActive : ""}`}
                  style={{ backgroundColor: hex }}
                  onClick={() => setFormData({ ...formData, color: hex })}
                  aria-label={`Cor ${hex}`}
                />
              ))}
              <Button
                variant="text"
                className={aeros.ghostButton}
                onClick={() => setColorModalOpen(true)}
              >
                {formData.color || "Outra cor"}
              </Button>
              {formData.color ? (
                <Button
                  variant="text"
                  className={aeros.ghostButton}
                  onClick={() => setFormData({ ...formData, color: "" })}
                >
                  Sem cor
                </Button>
              ) : null}
            </div>
            <ColorBoxModal
              open={colorModalOpen}
              handleClose={() => setColorModalOpen(false)}
              onChange={(color) => {
                // ColorBox pode devolver a string original (sem interação do
                // usuário) ou um objeto Color com .hex (sem "#") após o
                // usuário escolher uma cor.
                const raw = typeof color === "string" ? color : `#${color.hex}`;
                const hex = raw.startsWith("#") ? raw : `#${raw}`;
                setFormData({ ...formData, color: hex.toUpperCase() });
              }}
              currentColor={formData.color || "#A044FF"}
            />
          </div>
        </DialogContent>
        <DialogActions className={aeros.dialogActions}>
          <Button
            variant="text"
            className={aeros.ghostButton}
            onClick={handleCloseModal}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            className={aeros.gradientButtonSmall}
            disabled={loading}
          >
            {loading
              ? "Salvando..."
              : editingMotivo
              ? "Atualizar"
              : "Adicionar"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FinalizacaoAtendimento;
